import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { atLeastAccess } from "@/utils/accessLevel";
import { useAuth } from "@/hooks/useAuth";
import { loginRedirectState } from "@/utils/auth";
import { TrackList } from "@/components/tracks/TrackList";
import { TrackModal } from "@/components/tracks/TrackModal";
import { useTracks, useDeleteTrack } from "@/hooks/useTracks";
import { Track } from "@/types";
import axios from "axios";

export default function TracksPage() {
  const { t } = useTranslation("pages");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedTrack, setSelectedTrack] = useState<Track | undefined>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Track | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: tracks = [], isLoading, error } = useTracks();
  const deleteTrackMutation = useDeleteTrack();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track mutations are admin-only on the backend
  // (requireAccessLevel(AccessLevel.admin) in backend/src/routes/tracks.ts);
  // hide the affordances from everyone below admin.
  const isAdmin = atLeastAccess(user?.accessLevel, "admin");

  const handleCreateTrack = () => {
    if (!authLoading && !user) {
      navigate("/login", { state: loginRedirectState(location) });
      return;
    }

    setSelectedTrack(undefined);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEditTrack = (track: Track) => {
    setSelectedTrack(track);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleViewTrack = (track: Track) => {
    navigate(`/tracks/${track.id}`);
  };

  const handleViewLeaderboard = (track: Track) => {
    navigate(`/tracks/${track.id}/leaderboard`);
  };

  const handleDeleteTrack = (track: Track) => {
    setShowDeleteConfirm(track);
  };

  const confirmDelete = async () => {
    if (showDeleteConfirm) {
      try {
        await deleteTrackMutation.mutateAsync(showDeleteConfirm.id);
        setShowDeleteConfirm(null);
      } catch (error: unknown) {
        let message = t("tracks.deleteError");

        if (axios.isAxiosError(error)) {
          message =
            (error.response?.data as { error?: { message?: string } })?.error
              ?.message || message;
        } else if (error instanceof Error) {
          message = error.message;
        }

        setDeleteError(message);
      }
    }
  };

  if (error) {
    return (
      <div className="page-container">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">{t("tracks.errorLoading")}</h3>
          <p className="text-red-600 mt-1">
            {error instanceof Error ? error.message : t("tracks.unknownError")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex justify-between items-center mb-3 sm:mb-8">
        <div>
          <h1 className="page-title">{t("tracks.title")}</h1>
          <p className="page-subtitle">{t("tracks.pageSubtitle")}</p>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreateTrack}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {t("tracks.addTrack")}
          </button>
        )}
      </div>

      <TrackList
        tracks={tracks}
        isLoading={isLoading}
        onEdit={isAdmin ? handleEditTrack : undefined}
        onDelete={isAdmin ? handleDeleteTrack : undefined}
        onView={handleViewTrack}
        onLeaderboard={handleViewLeaderboard}
        emptyMessage={t("tracks.emptyMessage")}
      />

      {/* Track Modal */}
      <TrackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        track={selectedTrack}
        mode={modalMode}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              {t("tracks.deleteTitle")}
            </h3>
            <p className="text-secondary-600 mb-6">
              {t("tracks.deleteConfirmation", { name: showDeleteConfirm.name })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
                disabled={deleteTrackMutation.isPending}
              >
                {t("tracks.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteTrackMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleteTrackMutation.isPending ? t("tracks.deleting") : t("tracks.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion error alert */}
      {deleteError && (
 <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-3 rounded shadow-lg z-50 flex items-start gap-3 max-w-sm">
          <span>{deleteError}</span>
          <button
 className="ml-auto text-white/80 hover:text-white"
            onClick={() => setDeleteError(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
